import { useEffect } from 'react'

import { injectChipIcons } from '../chipIcons'
import { caseStudyImageSizes } from '../caseStudies'
import type {
  CaseChip,
  CaseFeature,
  CaseStudyContent,
  ImageSpec,
} from '../caseStudies/types'
import { Brand, SiteFooter } from './brand'

function isImageSpec(media: CaseFeature['media']): media is ImageSpec {
  return Boolean(media) && typeof media === 'object' && 'src' in (media as Record<string, unknown>)
}

function CaseImage({
  className,
  image,
  priority = false,
}: {
  className: string
  image: ImageSpec
  priority?: boolean
}) {
  const size = caseStudyImageSizes[image.src]

  return (
    <img
      src={image.src}
      alt={image.alt}
      className={className}
      decoding="async"
      fetchPriority={priority ? 'high' : 'auto'}
      height={size?.height}
      loading={priority ? 'eager' : 'lazy'}
      width={size?.width}
    />
  )
}

function Chips({ chips }: { chips: Array<CaseChip> }) {
  return (
    <div className="chips">
      {chips.map((chip) => (
        <span className={`chip${chip.key ? ' key' : ''}`} key={chip.label}>{chip.label}</span>
      ))}
    </div>
  )
}

export function CaseStudyLayout({ content }: { content: CaseStudyContent }) {
  useEffect(() => {
    injectChipIcons(document)
  }, [])

  return (
    <div className="case-page-body">
      <div className="page-glow" aria-hidden="true" />

      <nav className="case-nav">
        <div className="case-nav-inner">
          <Brand />
          <a className="back" href="/#work">← All work</a>
        </div>
      </nav>

      <header className="chero">
        <div className="wrap">
          <div className="kicker"><span className="sq" /> {content.kicker}</div>
          <h1>
            {content.liveHref ? (
              <a className="chero-title-link" href={content.liveHref} target="_blank" rel="noopener noreferrer">
                {content.title} <span className="ar">↗</span>
              </a>
            ) : (
              content.title
            )}
          </h1>
          <p className="lede">{content.lede}</p>
          <div className="meta">
            {content.meta.map((item) => (
              <div className="m" key={item.label}>
                <span className="ml">{item.label}</span>
                <span className="mv">{item.value}</span>
              </div>
            ))}
          </div>
          <Chips chips={content.chips} />
          {isImageSpec(content.heroImage) ? (
            <CaseImage image={content.heroImage} className="shot hero-shot" priority />
          ) : (
            <div className="hero-illus">{content.heroImage}</div>
          )}
        </div>
      </header>

      {/* OVERVIEW */}
      <section>
        <div className="wrap two">
          <div>
            <div className="sec-lbl">{content.overview.label}</div>
            <h2 className="sec-title">{content.overview.title}</h2>
          </div>
          <div>{content.overview.body}</div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section style={{ paddingTop: '20px' }}>
        <div className="wrap">
          <div className="sec-lbl">Highlights</div>
          <h2 className="sec-title" style={{ marginBottom: '30px' }}>{content.highlights.title}</h2>

          {content.highlights.features.map((feature, index) => (
            <div className={`feat${feature.flip ? ' flip' : ''}`} key={index}>
              <div className="feat-media">
                {isImageSpec(feature.media) ? (
                  <CaseImage image={feature.media} className="shot" />
                ) : (
                  feature.media
                )}
              </div>
              <div className="feat-body">
                <div className="feat-num">{feature.num}</div>
                <h3>{feature.title}</h3>
                <div className="feat-copy">{feature.body}</div>
                <ul className="pts">
                  {feature.points.map((point) => <li key={point}>{point}</li>)}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* UNDER THE HOOD */}
      <section className="hood">
        <div className="wrap">
          <div className="sec-lbl">Under the hood</div>
          <h2 className="sec-title">{content.hood.title}</h2>
          <div className="stack-grid">
            {content.hood.cards.map((card) => (
              <div className="scard" key={card.v}>
                <div className="t">{card.t}</div>
                <div className="v">{card.v}</div>
                <div className="d">{card.d}</div>
              </div>
            ))}
          </div>
          <p className="archnote">{content.hood.note}</p>
        </div>
      </section>

      {/* OUTCOME */}
      <section>
        <div className="wrap">
          <div className="sec-lbl">{content.outcome.label}</div>
          <h2 className="sec-title">{content.outcome.title}</h2>
          <div className="metrics">
            {content.outcome.metrics.map((metric, index) => (
              <div className={`metric${metric.sm ? ' sm' : ''}`} key={index}>
                <div className="n">{metric.n}</div>
                <div className="l">{metric.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* END NAV */}
      <nav className="endnav">
        <a className="prev" href={content.prev.href}>
          <span className="dir">{content.prev.dir}</span>
          <span className="ttl">{content.prev.ttl}</span>
        </a>
        <a className="next" href={content.next.href}>
          <span className="dir">{content.next.dir}</span>
          <span className="ttl">{content.next.ttl}</span>
        </a>
      </nav>

      <SiteFooter />
    </div>
  )
}
